import { useEffect, useRef } from 'preact/hooks';
import * as THREE from 'three';
import { decodeSkin } from '@vm/content';
import { PetState } from '@vm/shared';
import { PetRig, petMesh } from '../../game/render/petRig';
import { previewRenderer, type PreviewHandle, type PreviewScene } from './previewRenderer';
import { Spin } from './spin';
import { addStudioLights, makePedestal, makeShadowBlob, previewMaterials } from './stage';
import { IdlePose, VoxelMarine } from './voxelCharacter';
import '../../styles/three-ui.css';

/**
 * 3D preview of a Voxel Marine (skin + armor + held weapon) for menus.
 * Owned by the 3D-UI workstream; this is the interface contract.
 */
export interface CharacterPreviewProps {
  /** Base64 skin data or 'preset:<id>'. */
  skin: string;
  armor: string;
  /** Weapon id to hold (optional). */
  weapon?: string;
  /** Camo id for the held weapon (optional). */
  camo?: string;
  /** Fitted scope id for the held weapon (optional). */
  scope?: string;
  /** Companion pet standing beside the marine (optional), in its pet style. Drawn in the same 3D scene, so it keeps its true size. */
  pet?: string;
  petStyle?: string;
  /** Slowly auto-rotate; the user can always drag to rotate. */
  rotate?: boolean;
  className?: string;
  /** Core tint: -1 neutral (default), 0 team A, 1 team B. */
  team?: number;
  /** Show the plinth under the marine (default true). */
  pedestal?: boolean;
  /** Allow drag-to-rotate (default true). */
  interactive?: boolean;
  /** Starting turn in radians (default: a slight three-quarter view). */
  initialYaw?: number;
}

const TARGET_Y = 0.78;

/**
 * Companion pet, in marine space (meters; the plinth top is y = 0, the camera looks along +Z so +X is screen-left). It stands
 * on the screen-right, just off the plinth and toward the camera, at its real model size next to the 1.8 m marine.
 */
const PET_AT = { x: -0.66, z: -0.42, hover: 0.06 };
/** Three-quarter view of the pet's face, turned toward the marine (pets face -Z). */
const PET_YAW = -0.6;
/** Half the width the camera must keep in view: the marine with a weapon (0.76 m) or, with a pet, out to the pet's far side. */
const FIT_HALF_WIDTH = 0.76;
const FIT_HALF_WIDTH_PET = 1.08;

/** Scene for one character preview; drawn by the shared preview renderer. */
class CharacterStage implements PreviewScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(24, 1, 0.1, 40);
  readonly spin: Spin;
  private readonly turn = new THREE.Group();
  private readonly pedestal: THREE.Group;
  private marine: VoxelMarine | null = null;
  private readonly idle = new IdlePose();
  private key = '';
  private weaponId: string | undefined;
  private weaponCamo: string | undefined;
  private weaponScope: string | undefined;
  private readonly petGroup = new THREE.Group();
  private petRig: PetRig | null = null;
  private petKey = '';
  private petT = Math.random() * 10;
  private aspect = 1;
  private width = 1;
  private height = 1;
  private petBaseY = 0.3;

  constructor(initialYaw: number) {
    this.spin = new Spin(initialYaw);
    addStudioLights(this.scene);
    this.pedestal = makePedestal(0.6);
    this.turn.add(this.pedestal, makeShadowBlob(1.0, 0.8, 0.9));
    this.scene.add(this.turn, this.petGroup);
    this.petGroup.position.set(PET_AT.x, 0, PET_AT.z);
    this.resize(1, 1);
  }

  setPedestal(on: boolean): void {
    this.pedestal.visible = on;
  }

  setCharacter(skin: string, armor: string, team: number): void {
    const key = `${skin}\n${armor}\n${team}`;
    if (key === this.key) return;
    this.key = key;
    const next = new VoxelMarine(decodeSkin(skin), armor, team);
    next.setWeapon(this.weaponId, this.weaponCamo, this.weaponScope);
    if (this.marine) {
      this.turn.remove(this.marine.root);
      this.marine.dispose();
    }
    this.marine = next;
    this.turn.add(next.root);
  }

  setWeapon(id: string | undefined, camo: string | undefined, scope?: string): void {
    this.weaponId = id;
    this.weaponCamo = camo;
    this.weaponScope = scope;
    this.marine?.setWeapon(id, camo, scope);
  }

  /** Puts a companion pet beside the marine (undefined/unknown clears it). */
  setPet(petId: string | undefined, styleId: string | undefined): void {
    const key = petId ? `${petId}|${styleId ?? ''}` : '';
    if (key === this.petKey) return;
    this.petKey = key;
    if (this.petRig) {
      this.petGroup.remove(this.petRig.root);
      this.petRig.dispose();
      this.petRig = null;
    }
    const mesh = petId ? petMesh(petId, styleId || undefined) : null;
    if (mesh) {
      this.petRig = new PetRig(mesh, previewMaterials.lit, previewMaterials.glow);
      // The model's center is the rig origin: lift it so the lowest voxel hovers just above the plinth.
      let minY = Infinity;
      for (const g of [mesh.lit, mesh.glow]) {
        if (!g) continue;
        if (!g.boundingBox) g.computeBoundingBox();
        minY = Math.min(minY, g.boundingBox!.min.y);
      }
      this.petBaseY = (Number.isFinite(minY) ? -minY : 0.25) + PET_AT.hover;
      this.petRig.root.position.y = this.petBaseY;
      this.petRig.root.rotation.y = PET_YAW;
      this.petGroup.add(this.petRig.root);
    }
    this.resize(this.width, this.height);
  }

  tick(dt: number): boolean {
    this.spin.tick(dt);
    this.turn.rotation.y = this.spin.yaw;
    if (this.marine) this.idle.update(this.marine, dt);
    if (this.petRig) {
      this.petT += dt;
      this.petRig.animate(dt, PetState.Follow);
      this.petRig.root.position.y = this.petBaseY + Math.sin(this.petT * 2.1) * 0.02;
      this.petRig.body.rotation.z = Math.sin(this.petT * 1.3) * 0.05;
    }
    return true;
  }

  resize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    const aspect = w / Math.max(1, h);
    this.aspect = aspect;
    const cam = this.camera;
    cam.aspect = aspect;
    const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
    // Fit the marine + plinth (about 1.9 m tall, 1.4 m wide incl. a held weapon), and the pet beside it when there is one.
    const dist = Math.max(1.1 / t, (this.petRig ? FIT_HALF_WIDTH_PET : FIT_HALF_WIDTH) / (t * aspect));
    const elev = 0.13;
    cam.position.set(0, TARGET_Y + Math.sin(elev) * dist, -Math.cos(elev) * dist);
    cam.lookAt(0, TARGET_Y, 0);
    cam.updateProjectionMatrix();
  }

  dispose(): void {
    this.marine?.dispose();
    this.marine = null;
    if (this.petRig) {
      this.petGroup.remove(this.petRig.root);
      this.petRig.dispose();
      this.petRig = null;
    }
  }
}

export function CharacterPreview(props: CharacterPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<CharacterStage | null>(null);
  const handleRef = useRef<PreviewHandle | null>(null);

  if (!stageRef.current) stageRef.current = new CharacterStage(props.initialYaw ?? -0.42);
  const stage = stageRef.current;

  useEffect(() => {
    const canvas = canvasRef.current!;
    const handle = previewRenderer.attach(canvas, stage);
    handleRef.current = handle;
    return () => {
      handle.detach();
      handleRef.current = null;
      stage.dispose();
      stageRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (props.interactive === false) return;
    return stage.spin.attach(canvasRef.current!, (on) => handleRef.current?.setInteracting(on));
  }, [props.interactive]);

  useEffect(() => {
    stage.setCharacter(props.skin, props.armor, props.team ?? -1);
    handleRef.current?.invalidate();
  }, [props.skin, props.armor, props.team]);

  useEffect(() => {
    stage.setPet(props.pet, props.petStyle);
    handleRef.current?.invalidate();
  }, [props.pet, props.petStyle]);

  useEffect(() => {
    stage.setWeapon(props.weapon, props.camo, props.scope);
    handleRef.current?.invalidate();
  }, [props.weapon, props.camo, props.scope]);

  useEffect(() => {
    stage.spin.auto = props.rotate ? 0.45 : 0;
    stage.setPedestal(props.pedestal !== false);
    handleRef.current?.invalidate();
  }, [props.rotate, props.pedestal]);

  return (
    <div class={`char-preview three-preview ${props.className ?? ''}`}>
      <canvas ref={canvasRef} class={`three-canvas${props.interactive === false ? '' : ' spinnable'}`} />
    </div>
  );
}
